import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api, unwrap, unwrapVoid, type Schemas } from "./client";

export type Timeline = Schemas["Timeline"];
export type TimelineSlot = Schemas["TimelineSlot"];
export type TeamMember = Schemas["TeamMember"];
export type Project = Schemas["Project"];
export type UsageItem = Schemas["UsageItem"];
export type AppCategory = Schemas["AppCategory"];
export type Screenshot = Schemas["Screenshot"];

export function useTeamStatus(teamId?: string) {
  return useQuery({
    queryKey: ["team", teamId ?? "all"],
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET("/api/v1/activity/team", {
          params: { query: teamId ? { team_id: teamId } : {} },
          signal,
        }),
      ),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });
}

export type Dashboard = Schemas["Dashboard"];
export type DayStats = Schemas["DayStats"];
export type PeriodTotals = Schemas["PeriodTotals"];
export type AttentionItem = Schemas["AttentionItem"];

export function useDashboard(teamId?: string) {
  return useQuery({
    queryKey: ["dashboard", teamId ?? "all"],
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET("/api/v1/dashboard", {
          params: { query: teamId ? { team_id: teamId } : {} },
          signal,
        }),
      ),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
}

export type AppsReport = Schemas["AppsReport"];
export type AppsReportItem = Schemas["AppsReportItem"];
export type AppsScope = { from: string; to: string; userIds?: string[]; teamId?: string };

export function useAppsReport(scope: AppsScope) {
  return useQuery({
    queryKey: ["apps-report", scope],
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET("/api/v1/activity/apps", {
          params: {
            query: {
              from: scope.from,
              to: scope.to,
              user_id: scope.userIds?.length ? scope.userIds : undefined,
              team_id: scope.teamId,
            },
          },
          signal,
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

export type AppCatalogEntry = Schemas["AppCatalogEntry"];

/** Everything that shows categories or productive time. */
function useInvalidateCategories() {
  const qc = useQueryClient();
  return () =>
    Promise.all(
      [["app-catalog"], ["apps-report"], ["dashboard"], ["timeline"]].map((queryKey) =>
        qc.invalidateQueries({ queryKey }),
      ),
    );
}

export function useAppCatalog(enabled = true) {
  return useQuery({
    queryKey: ["app-catalog"],
    enabled,
    queryFn: async ({ signal }) => unwrap(await api.GET("/api/v1/app-catalog", { signal })).items,
  });
}

export type CatalogInput =
  | ({ entryId?: undefined } & Schemas["AppCatalogCreate"])
  | ({ entryId: string } & Schemas["AppCatalogUpdate"]);

export function useSaveCatalogEntry() {
  const invalidate = useInvalidateCategories();
  return useMutation({
    mutationFn: async (input: CatalogInput) => {
      if (input.entryId === undefined) {
        const { kind, match_value, display_name, category } = input;
        return unwrap(
          await api.POST("/api/v1/app-catalog", { body: { kind, match_value, display_name, category } }),
        );
      }
      const { entryId, display_name, category } = input;
      return unwrap(
        await api.PATCH("/api/v1/app-catalog/{entryId}", {
          params: { path: { entryId } },
          body: { display_name, category },
        }),
      );
    },
    onSuccess: invalidate,
  });
}

export function useDeleteCatalogEntry() {
  const invalidate = useInvalidateCategories();
  return useMutation({
    mutationFn: async (entryId: string) =>
      unwrapVoid(await api.DELETE("/api/v1/app-catalog/{entryId}", { params: { path: { entryId } } })),
    onSuccess: invalidate,
  });
}

export function useSetCategoryOverride() {
  const invalidate = useInvalidateCategories();
  return useMutation({
    mutationFn: async ({
      entryId,
      teamId,
      category,
    }: {
      entryId: string;
      teamId: string;
      category: AppCategory | null;
    }) => {
      const params = { path: { entryId, teamId } };
      return category
        ? unwrap(
            await api.PUT("/api/v1/app-catalog/{entryId}/teams/{teamId}", { params, body: { category } }),
          )
        : unwrap(await api.DELETE("/api/v1/app-catalog/{entryId}/teams/{teamId}", { params }));
    },
    onSuccess: invalidate,
  });
}

export function useRestoreSlotTime() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ slotId }: { slotId: string }) =>
      unwrapVoid(await api.POST("/api/v1/slots/{slotId}/restore", { params: { path: { slotId } } })),
    onSuccess: () =>
      Promise.all(
        [["timeline"], ["dashboard"], ["team"], ["apps-report"]].map((queryKey) =>
          qc.invalidateQueries({ queryKey }),
        ),
      ),
  });
}

export function useDeleteScreenshot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ screenshotId, reason }: { screenshotId: string; reason: string }) =>
      unwrapVoid(
        await api.POST("/api/v1/screenshots/{screenshotId}/delete", {
          params: { path: { screenshotId } },
          body: { reason },
        }),
      ),
    onSuccess: () =>
      Promise.all(
        [["timeline"], ["dashboard"], ["team"], ["apps-report"]].map((queryKey) =>
          qc.invalidateQueries({ queryKey }),
        ),
      ),
  });
}

export function useUsers() {
  return useQuery({
    queryKey: ["users"],
    queryFn: async ({ signal }) => unwrap(await api.GET("/api/v1/users", { signal })).items,
  });
}

export function useTimeline(userId: string | undefined, date: string) {
  return useQuery({
    queryKey: ["timeline", userId, date],
    enabled: Boolean(userId),
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET("/api/v1/users/{userId}/timeline", {
          params: { path: { userId: userId ?? "" }, query: { date } },
          signal,
        }),
      ),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
}

export type Task = Schemas["Task"];
export type TaskStatus = Schemas["TaskStatus"];
export type TaskInput = Schemas["TaskInput"];
export type TaskList = Schemas["TaskList"];
export type ProjectRef = Schemas["ProjectRef"];

// Query keys: ["projects"] prefixes every project query, ["tasks"] every task list.
const projectKeys = {
  all: ["projects"] as const,
  one: (id: string) => ["projects", id] as const,
  stats: (id: string) => ["projects", id, "stats"] as const,
};
const taskKeys = {
  all: ["tasks"] as const,
  project: (id: string) => ["tasks", "project", id] as const,
  mine: (statuses: readonly TaskStatus[]) => ["tasks", "mine", ...statuses] as const,
};

export function useProjects() {
  return useQuery({
    queryKey: projectKeys.all,
    queryFn: async ({ signal }) => unwrap(await api.GET("/api/v1/projects", { signal })).items,
  });
}

export function useProject(projectId: string) {
  return useQuery({
    queryKey: projectKeys.one(projectId),
    queryFn: async ({ signal }) =>
      unwrap(await api.GET("/api/v1/projects/{projectId}", { params: { path: { projectId } }, signal })),
  });
}

export function useProjectStats(projectId: string) {
  return useQuery({
    queryKey: projectKeys.stats(projectId),
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET("/api/v1/projects/{projectId}/stats", { params: { path: { projectId } }, signal }),
      ),
  });
}

export function useSaveProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectId, body }: { projectId?: string; body: Schemas["ProjectInput"] }) =>
      projectId
        ? unwrap(await api.PATCH("/api/v1/projects/{projectId}", { params: { path: { projectId } }, body }))
        : unwrap(await api.POST("/api/v1/projects", { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: projectKeys.all }),
  });
}

export function useSetProjectStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectId, status }: { projectId: string; status: Project["status"] }) =>
      unwrap(
        await api.PUT("/api/v1/projects/{projectId}/status", {
          params: { path: { projectId } },
          body: { status },
        }),
      ),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: projectKeys.all }),
        qc.invalidateQueries({ queryKey: taskKeys.all }),
      ]),
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (projectId: string) =>
      unwrapVoid(await api.DELETE("/api/v1/projects/{projectId}", { params: { path: { projectId } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: projectKeys.all }),
  });
}

export function useSetProjectMembers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectId,
      members,
    }: {
      projectId: string;
      members: Schemas["ProjectMemberRef"][];
    }) =>
      unwrap(
        await api.PUT("/api/v1/projects/{projectId}/members", {
          params: { path: { projectId } },
          body: { members },
        }),
      ),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: projectKeys.all }),
        // Removed members lose their tasks; managers see new people.
        qc.invalidateQueries({ queryKey: taskKeys.all }),
        qc.invalidateQueries({ queryKey: ["users"] }),
      ]),
  });
}

export function useProjectTasks(projectId: string, enabled = true) {
  return useQuery({
    queryKey: taskKeys.project(projectId),
    enabled: enabled && Boolean(projectId),
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET("/api/v1/projects/{projectId}/tasks", { params: { path: { projectId } }, signal }),
      ),
  });
}

export function useMyTasks(statuses: readonly TaskStatus[]) {
  return useQuery({
    queryKey: taskKeys.mine(statuses),
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET("/api/v1/tasks", {
          params: { query: { assignee_id: "me", status: statuses.length ? [...statuses] : undefined } },
          signal,
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

function useInvalidateTasks() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: taskKeys.all }),
      // Task counts are part of the project.
      qc.invalidateQueries({ queryKey: projectKeys.all }),
    ]);
}

export function useSaveTask() {
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: async ({
      projectId,
      taskId,
      body,
    }: {
      projectId: string;
      taskId?: string;
      body: TaskInput;
    }) =>
      taskId
        ? unwrap(await api.PATCH("/api/v1/tasks/{taskId}", { params: { path: { taskId } }, body }))
        : unwrap(
            await api.POST("/api/v1/projects/{projectId}/tasks", { params: { path: { projectId } }, body }),
          ),
    onSuccess: invalidate,
  });
}

/** Moves a task; task lists are updated at once and rolled back on failure. */
export function useSetTaskStatus() {
  const qc = useQueryClient();
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: async ({ taskId, status }: { taskId: string; status: TaskStatus }) =>
      unwrap(
        await api.PUT("/api/v1/tasks/{taskId}/status", { params: { path: { taskId } }, body: { status } }),
      ),
    onMutate: async ({ taskId, status }) => {
      await qc.cancelQueries({ queryKey: taskKeys.all });
      const previous = qc.getQueriesData<TaskList>({ queryKey: taskKeys.all });
      qc.setQueriesData<TaskList>(
        { queryKey: taskKeys.all },
        (list) => list && { ...list, items: list.items.map((t) => (t.id === taskId ? { ...t, status } : t)) },
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      for (const [key, data] of context?.previous ?? []) qc.setQueryData(key, data);
    },
    onSettled: invalidate,
  });
}

export function useDeleteTask() {
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: async (taskId: string) =>
      unwrapVoid(await api.DELETE("/api/v1/tasks/{taskId}", { params: { path: { taskId } } })),
    onSuccess: invalidate,
  });
}

export type User = Schemas["User"];
export type Me = Schemas["Me"];
export type Team = Schemas["Team"];
export type Device = Schemas["Device"];
export type Company = Schemas["Company"];
export type LinkResult = Schemas["LinkResult"];
export type AuditEntry = Schemas["AuditEntry"];
export type Role = Schemas["Role"];

// ---------------------------------------------------------------- profile

export function useUpdateMe() {
  return useMutation({
    mutationFn: async (body: Schemas["ProfileUpdate"]) => unwrap(await api.PATCH("/api/v1/me", { body })),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (body: Schemas["ChangePasswordRequest"]) =>
      unwrapVoid(await api.POST("/api/v1/me/password", { body })),
  });
}

export function useStartMfaSetup() {
  return useMutation({
    mutationFn: async () => unwrap(await api.POST("/api/v1/me/mfa/setup", {})),
  });
}

export function useEnableMfa() {
  return useMutation({
    mutationFn: async (body: Schemas["MfaEnableRequest"]) =>
      unwrapVoid(await api.POST("/api/v1/me/mfa", { body })),
  });
}

export function useDisableMfa() {
  return useMutation({
    mutationFn: async (body: Schemas["PasswordConfirmation"]) =>
      unwrapVoid(await api.POST("/api/v1/me/mfa/disable", { body })),
  });
}

export function useDevices(userId: string) {
  return useQuery({
    queryKey: ["devices", userId],
    queryFn: async ({ signal }) =>
      unwrap(await api.GET("/api/v1/users/{userId}/devices", { params: { path: { userId } }, signal })).items,
  });
}

export function useRevokeDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (deviceId: string) =>
      unwrapVoid(
        await api.DELETE("/api/v1/devices/{deviceId}", {
          params: { path: { deviceId } },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["devices"] }),
  });
}

// ------------------------------------------------------------------ people

function useInvalidatePeople() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ["users"] }),
      qc.invalidateQueries({ queryKey: ["teams"] }),
      qc.invalidateQueries({ queryKey: ["team"] }),
    ]);
}

export function useInviteUser() {
  const invalidate = useInvalidatePeople();
  return useMutation({
    mutationFn: async (body: Schemas["InviteRequest"]) =>
      unwrap(await api.POST("/api/v1/users/invitations", { body })),
    onSuccess: invalidate,
  });
}

export function useUpdateUser() {
  const invalidate = useInvalidatePeople();
  return useMutation({
    mutationFn: async ({ userId, ...body }: Schemas["UserUpdate"] & { userId: string }) =>
      unwrap(await api.PATCH("/api/v1/users/{userId}", { params: { path: { userId } }, body })),
    onSuccess: invalidate,
  });
}

export function useCancelInvitation() {
  const invalidate = useInvalidatePeople();
  return useMutation({
    mutationFn: async (userId: string) =>
      unwrapVoid(await api.DELETE("/api/v1/users/{userId}", { params: { path: { userId } } })),
    onSuccess: invalidate,
  });
}

export function useResendInvitation() {
  const invalidate = useInvalidatePeople();
  return useMutation({
    mutationFn: async (userId: string) =>
      unwrap(await api.POST("/api/v1/users/{userId}/invitation", { params: { path: { userId } } })),
    onSuccess: invalidate,
  });
}

export function useCreateResetLink() {
  return useMutation({
    mutationFn: async (userId: string) =>
      unwrap(await api.POST("/api/v1/users/{userId}/password-reset", { params: { path: { userId } } })),
  });
}

export function useResetUserMfa() {
  const invalidate = useInvalidatePeople();
  return useMutation({
    mutationFn: async (userId: string) =>
      unwrapVoid(await api.DELETE("/api/v1/users/{userId}/mfa", { params: { path: { userId } } })),
    onSuccess: invalidate,
  });
}

// ------------------------------------------------------------------- teams

export function useTeams() {
  return useQuery({
    queryKey: ["teams"],
    queryFn: async ({ signal }) => unwrap(await api.GET("/api/v1/teams", { signal })).items,
  });
}

export function useSaveTeam() {
  const invalidate = useInvalidatePeople();
  return useMutation({
    mutationFn: async ({ teamId, ...body }: Schemas["TeamInput"] & { teamId?: string }) =>
      teamId
        ? unwrap(await api.PATCH("/api/v1/teams/{teamId}", { params: { path: { teamId } }, body }))
        : unwrap(await api.POST("/api/v1/teams", { body })),
    onSuccess: invalidate,
  });
}

export function useDeleteTeam() {
  const invalidate = useInvalidatePeople();
  return useMutation({
    mutationFn: async (teamId: string) =>
      unwrapVoid(await api.DELETE("/api/v1/teams/{teamId}", { params: { path: { teamId } } })),
    onSuccess: invalidate,
  });
}

export function useSetTeamMembers() {
  const invalidate = useInvalidatePeople();
  return useMutation({
    mutationFn: async ({ teamId, members }: { teamId: string; members: Schemas["TeamMemberRef"][] }) =>
      unwrap(
        await api.PUT("/api/v1/teams/{teamId}/members", {
          params: { path: { teamId } },
          body: { members },
        }),
      ),
    onSuccess: invalidate,
  });
}

// ------------------------------------------------------------------- admin

export function useCompany() {
  return useQuery({
    queryKey: ["company"],
    queryFn: async ({ signal }) => unwrap(await api.GET("/api/v1/company", { signal })),
  });
}

export function useUpdateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Schemas["CompanyUpdate"]) =>
      unwrap(await api.PATCH("/api/v1/company", { body })),
    onSuccess: (data) => qc.setQueryData(["company"], data),
  });
}

export type AuditFilter = {
  action?: string;
  actorId?: string;
  subjectId?: string;
  /** RFC 3339 instants: [from, to). */
  from?: string;
  to?: string;
};

export function useAuditLog(filter: AuditFilter) {
  return useInfiniteQuery({
    queryKey: ["audit", filter],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam, signal }) =>
      unwrap(
        await api.GET("/api/v1/audit-log", {
          params: {
            query: {
              limit: 50,
              cursor: pageParam,
              action: filter.action || undefined,
              actor_id: filter.actorId || undefined,
              subject_id: filter.subjectId || undefined,
              from: filter.from || undefined,
              to: filter.to || undefined,
            },
          },
          signal,
        }),
      ),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
}

// ---------------------------------------------------------------- policies

export type PolicySettings = Schemas["PolicySettings"];
export type PolicyOverrides = Schemas["PolicyOverrides"];
export type PolicySource = Schemas["PolicySource"];
export type CompanyPolicy = Schemas["CompanyPolicy"];
export type LevelPolicy = Schemas["LevelPolicy"];
export type UserPolicy = Schemas["UserPolicy"];

// ["policies"] prefixes every policy query: a change on one level changes the others' effective values.
const policyKeys = {
  all: ["policies"] as const,
  company: ["policies", "company"] as const,
  team: (id: string) => ["policies", "team", id] as const,
  user: (id: string) => ["policies", "user", id] as const,
};

export function useCompanyPolicy() {
  return useQuery({
    queryKey: policyKeys.company,
    queryFn: async ({ signal }) => unwrap(await api.GET("/api/v1/policies/company", { signal })),
  });
}

export function useTeamPolicy(teamId: string) {
  return useQuery({
    queryKey: policyKeys.team(teamId),
    enabled: Boolean(teamId),
    queryFn: async ({ signal }) =>
      unwrap(await api.GET("/api/v1/teams/{teamId}/policy", { params: { path: { teamId } }, signal })),
  });
}

export function useUserPolicy(userId: string) {
  return useQuery({
    queryKey: policyKeys.user(userId),
    enabled: Boolean(userId),
    queryFn: async ({ signal }) =>
      unwrap(await api.GET("/api/v1/users/{userId}/policy", { params: { path: { userId } }, signal })),
  });
}

export type PolicyTarget = { level: "company" } | { level: "team" | "user"; id: string };

export function useSavePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ target, values }: { target: PolicyTarget; values: PolicyOverrides }) => {
      switch (target.level) {
        case "company":
          return unwrap(await api.PUT("/api/v1/policies/company", { body: values as PolicySettings }));
        case "team":
          return unwrap(
            await api.PUT("/api/v1/teams/{teamId}/policy", {
              params: { path: { teamId: target.id } },
              body: values,
            }),
          );
        case "user":
          return unwrap(
            await api.PUT("/api/v1/users/{userId}/policy", {
              params: { path: { userId: target.id } },
              body: values,
            }),
          );
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: policyKeys.all }),
  });
}

// ------------------------------------------------------ tracker sign-in

export type DeviceAuthRequest = Schemas["DeviceAuthRequest"];

export function useDeviceAuth(code: string) {
  return useQuery({
    queryKey: ["deviceAuth", code],
    enabled: code !== "",
    retry: false,
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET("/api/v1/device-auth/{userCode}", { params: { path: { userCode: code } }, signal }),
      ),
  });
}

export function useDecideDeviceAuth() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ code, approve }: { code: string; approve: boolean }) => {
      const params = { params: { path: { userCode: code } } };
      return unwrapVoid(
        approve
          ? await api.POST("/api/v1/device-auth/{userCode}/approve", params)
          : await api.POST("/api/v1/device-auth/{userCode}/deny", params),
      );
    },
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["deviceAuth"] }),
        qc.invalidateQueries({ queryKey: ["devices"] }),
      ]),
  });
}

// ------------------------------------------------------------ manual time

export type ManualTimeRequest = Schemas["ManualTimeRequest"];
export type ManualTimeStatus = Schemas["ManualTimeStatus"];
export type Timesheet = Schemas["Timesheet"];
export type TeamTimesheet = Schemas["TeamTimesheet"];
export type Vacation = Schemas["Vacation"];
export type VacationItem = Schemas["VacationItem"];
export type AppNotification = Schemas["Notification"];

export type ManualTimeFilter = {
  view: "mine" | "review";
  status?: ManualTimeStatus;
  userId?: string;
  from?: string;
  to?: string;
};

export function useManualTime(filter: ManualTimeFilter, enabled = true) {
  return useQuery({
    queryKey: ["manual-time", filter],
    enabled,
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET("/api/v1/manual-time", {
          params: {
            query: {
              view: filter.view,
              status: filter.status,
              user_id: filter.userId,
              from: filter.from,
              to: filter.to,
            },
          },
          signal,
        }),
      ).items,
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
}

/** Everything that shows worked time. */
function useInvalidateWorkTime() {
  const qc = useQueryClient();
  return () =>
    Promise.all(
      [
        "manual-time",
        "timesheet",
        "team-timesheet",
        "timeline",
        "dashboard",
        "notifications",
        "projects",
      ].map((key) => qc.invalidateQueries({ queryKey: [key] })),
    );
}

export function useCreateManualTime() {
  const invalidate = useInvalidateWorkTime();
  return useMutation({
    mutationFn: async (body: Schemas["ManualTimeCreate"]) =>
      unwrap(await api.POST("/api/v1/manual-time", { body })),
    onSuccess: invalidate,
  });
}

export function useReviewManualTime() {
  const invalidate = useInvalidateWorkTime();
  return useMutation({
    mutationFn: async ({ id, approve, comment }: { id: string; approve: boolean; comment?: string }) => {
      const params = { path: { requestId: id } };
      const body = { comment: comment || undefined };
      return unwrap(
        approve
          ? await api.POST("/api/v1/manual-time/{requestId}/approve", { params, body })
          : await api.POST("/api/v1/manual-time/{requestId}/reject", { params, body }),
      );
    },
    onSuccess: invalidate,
  });
}

export function useCancelManualTime() {
  const invalidate = useInvalidateWorkTime();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrap(
        await api.POST("/api/v1/manual-time/{requestId}/cancel", { params: { path: { requestId: id } } }),
      ),
    onSuccess: invalidate,
  });
}

export function useTimesheet(userId: string | undefined, from: string, to: string) {
  return useQuery({
    queryKey: ["timesheet", userId ?? "me", from, to],
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET("/api/v1/timesheets", { params: { query: { user_id: userId, from, to } }, signal }),
      ),
    placeholderData: keepPreviousData,
  });
}

export function useTeamTimesheet(teamId: string | undefined, from: string, to: string) {
  return useQuery({
    queryKey: ["team-timesheet", teamId ?? "all", from, to],
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET("/api/v1/timesheets/team", {
          params: { query: { team_id: teamId, from, to } },
          signal,
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

// --------------------------------------------------------------- vacations

export function useVacations() {
  return useQuery({
    queryKey: ["vacations"],
    queryFn: async ({ signal }) => unwrap(await api.GET("/api/v1/vacations", { signal })).items,
  });
}

/** Sets (with `from`) or clears (without) a vacation mark; returns the updated user. */
export function useSaveVacation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, from, until }: { userId: string; from?: string; until?: string }) => {
      const params = { path: { userId } };
      return unwrap(
        from
          ? await api.PUT("/api/v1/users/{userId}/vacation", { params, body: { from, until } })
          : await api.DELETE("/api/v1/users/{userId}/vacation", { params }),
      );
    },
    onSuccess: () =>
      Promise.all(
        [
          "vacations",
          "users",
          "team",
          "dashboard",
          "team-timesheet",
          "timesheet",
          "timeline",
          "notifications",
        ].map((key) => qc.invalidateQueries({ queryKey: [key] })),
      ),
  });
}

// ----------------------------------------------------------- notifications

const notificationsPage = 20;

export function useNotifications() {
  return useInfiniteQuery({
    queryKey: ["notifications"],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam, signal }) =>
      unwrap(
        await api.GET("/api/v1/notifications", {
          params: { query: { before: pageParam, limit: notificationsPage } },
          signal,
        }),
      ),
    getNextPageParam: (last) =>
      last.items.length < notificationsPage ? undefined : last.items[last.items.length - 1]?.created_at,
    refetchInterval: 60_000,
  });
}

export function useMarkNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids?: string[]) =>
      unwrapVoid(await api.POST("/api/v1/notifications/read", { body: ids ? { ids } : {} })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
}

// ---------------------------------------------------------------- reports

export type TimeReport = Schemas["TimeReport"];
export type TimeReportRow = Schemas["TimeReportRow"];
export type TimeReportGroup = Schemas["TimeReportGroup"];
export type ActivityReport = Schemas["ActivityReport"];
export type ActivityReportRow = Schemas["ActivityReportRow"];
export type ActivityReportGroup = Schemas["ActivityReportGroup"];
export type TimeOffReport = Schemas["TimeOffReport"];
export type AnomalyReport = Schemas["AnomalyReport"];
export type ReportKind = Schemas["ReportKind"];
export type ReportExport = Schemas["ReportExport"];
export type ExportFormat = Schemas["ExportFormat"];

/** People, team and projects of a report; the period is local days. */
export type ReportScope = {
  from: string;
  to: string;
  teamId?: string;
  userIds?: string[];
  projectIds?: string[];
};

function scopeQuery(s: ReportScope) {
  return {
    from: s.from,
    to: s.to,
    team_id: s.teamId,
    user_id: s.userIds?.length ? s.userIds : undefined,
    project_id: s.projectIds?.length ? s.projectIds : undefined,
  };
}

export function useTimeReport(scope: ReportScope, groupBy: TimeReportGroup[], enabled = true) {
  return useQuery({
    queryKey: ["report", "time", scope, groupBy],
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET("/api/v1/reports/time", {
          params: { query: { ...scopeQuery(scope), group_by: groupBy } },
          signal,
        }),
      ),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useActivityReport(scope: ReportScope, groupBy: ActivityReportGroup[], enabled = true) {
  return useQuery({
    queryKey: ["report", "activity", scope, groupBy],
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET("/api/v1/reports/activity", {
          params: { query: { ...scopeQuery(scope), group_by: groupBy } },
          signal,
        }),
      ),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useTimeOffReport(scope: ReportScope, enabled = true) {
  return useQuery({
    queryKey: ["report", "time-off", scope],
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET("/api/v1/reports/time-off", {
          params: { query: scopeQuery({ ...scope, projectIds: undefined }) },
          signal,
        }),
      ),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useAnomaliesReport(scope: ReportScope, enabled = true) {
  return useQuery({
    queryKey: ["report", "anomalies", scope],
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET("/api/v1/reports/anomalies", {
          params: { query: scopeQuery({ ...scope, projectIds: undefined }) },
          signal,
        }),
      ),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useCreateExport() {
  return useMutation({
    mutationFn: async (body: Schemas["ReportExportCreate"]) =>
      unwrap(await api.POST("/api/v1/reports/exports", { body })),
  });
}

/** Polls an export until the worker has finished it. */
export function useExport(id: string | undefined) {
  return useQuery({
    queryKey: ["export", id],
    queryFn: async ({ signal }) =>
      unwrap(
        await api.GET("/api/v1/reports/exports/{exportId}", {
          params: { path: { exportId: id ?? "" } },
          signal,
        }),
      ),
    enabled: Boolean(id),
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      return status === "done" || status === "failed" ? false : 1000;
    },
  });
}

/** Downloads a ready export file as a blob. */
export async function fetchExportFile(id: string): Promise<Blob> {
  const result = await api.GET("/api/v1/reports/exports/{exportId}/file", {
    params: { path: { exportId: id } },
    parseAs: "blob",
  });
  return unwrap(result as { data?: Blob; error?: unknown; response: Response });
}

// ---------------------------------------------------------------- tracker releases

export type AgentRelease = Schemas["AgentRelease"];
export type CpuArch = Schemas["CpuArch"];

export function useAgentReleases() {
  return useQuery({
    queryKey: ["agent-releases"],
    queryFn: async ({ signal }) => unwrap(await api.GET("/api/v1/agent-releases", { signal })).items,
  });
}

export function useCreateRelease() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Schemas["AgentReleaseCreate"]) =>
      unwrap(await api.POST("/api/v1/agent-releases", { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agent-releases"] }),
  });
}

export function useUpdateRelease() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: Schemas["AgentReleaseUpdate"] & { id: string }) =>
      unwrap(
        await api.PATCH("/api/v1/agent-releases/{releaseId}", { params: { path: { releaseId: id } }, body }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agent-releases"] }),
  });
}

export function useDeleteRelease() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrapVoid(
        await api.DELETE("/api/v1/agent-releases/{releaseId}", { params: { path: { releaseId: id } } }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agent-releases"] }),
  });
}

// ---------------------------------------------------------------- system

export type SystemStatus = Schemas["SystemStatus"];
export type HealthLevel = Schemas["HealthLevel"];

export function useSystemStatus() {
  return useQuery({
    queryKey: ["system-status"],
    queryFn: async ({ signal }) => unwrap(await api.GET("/api/v1/system/status", { signal })),
    refetchInterval: 60_000,
  });
}
